import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArchiveBoxIcon, ArrowTopRightOnSquareIcon, ArrowUpTrayIcon, CheckCircleIcon,
  ExclamationTriangleIcon, InformationCircleIcon, ScaleIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { openProSearch } from '../../utils/proSearch';
import {
  applyFile, CHUNK_SIZE, extractDetail, getReferenceData, validateFile,
  VERDICT_LABELS,
  type ApplyResponse, type ContainerPreview, type DaltileOtwReferenceData,
  type FileError, type ValidateResponse, type Verdict,
} from '../../services/daltileOtwImport.service';

/**
 * Dal Tile OTW Upload — 360 port of v1 imports/otw_dalt_updated.pl.
 *
 * Upload the OTW workbook, read what the run will do, then run it. The unit
 * throughout is the CONTAINER, not the file row — and here that matters more
 * than it does for Home Depot: Dal Tile's rows are per PURCHASE ORDER, so a
 * container's weight and pallet count are the SUM of its rows, and each distinct
 * PO becomes one master-receiving line on the pro.
 *
 * There are no per-row checkboxes. An OTW run always creates what is new and
 * always updates what has changed — the preview exists to be read, not chosen
 * from. Any validation error rejects the whole file; nothing is written
 * partially.
 */

const VERDICT_ORDER: Verdict[] = [
  'create', 'update', 'no_change', 'old_move', 'delivered', 'deleted',
];

const VERDICT_STYLES: Record<Verdict, string> = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  no_change: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  old_move: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  delivered: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

// How many container rows the plan table renders at once.
//
// A real Dal Tile file is far bigger than a Home Depot one: the 2026-09-15
// production export collates to 1,889 containers, which is ~21,000 table cells
// and a visibly slow page for a list nobody scrolls. The rows that matter are
// always a small minority — that run had 238 to write — and the verdict filters
// above the table are how you reach the rest, so the table shows the first slice
// and says how many it is holding back.
const MAX_ROWS = 300;

/** m:ss, so a run that takes minutes reads as minutes. */
const formatElapsed = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const VerdictPill: React.FC<{ v: Verdict }> = ({ v }) => (
  <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${VERDICT_STYLES[v]}`}>
    {VERDICT_LABELS[v]}
  </span>
);

// Where each kind of problem is fixed. Nearly every failure this importer can
// report is a missing row on one of these pages rather than bad data in the
// file, so the list is worth showing outright instead of leaving the user to
// work out which maintenance screen "not mapped" refers to.
const CONFIG_PAGES: { to: string; label: string }[] = [
  { to: '/data-management/dal-tile-template-mapping', label: 'Dal Tile Template Mapping' },
  { to: '/data-management/upload-port-mapping', label: 'Upload Port Mapping' },
  { to: '/data-management/upload-broker-mapping', label: 'Upload Broker Mapping' },
  { to: '/data-management/dal-tile-carrier-mapping', label: 'Dal Tile Carrier Mapping' },
];

// One entry per CAUSE. A bad mapping errors on every row that uses it, so the
// count plus a few sample rows says far more than one line per row.
const ErrorList: React.FC<{ errors: FileError[] }> = ({ errors }) => {
  const affected = errors.reduce((n, e) => n + (e.count || 0), 0);
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
        <ExclamationTriangleIcon className="h-5 w-5" />
        Nothing was imported — {errors.length} problem{errors.length === 1 ? '' : 's'} to fix
        {affected > 0 && `, affecting ${affected} row${affected === 1 ? '' : 's'}`}.
      </div>
      <ul className="space-y-2 text-sm text-red-800 dark:text-red-300">
        {errors.map((e, i) => (
          <li key={i} className="leading-snug">
            <span>{e.message}</span>
            {e.count > 1 && (
              <span className="ml-1.5 whitespace-nowrap rounded-full bg-red-200 px-1.5 py-0.5 text-xs font-semibold dark:bg-red-800">
                {e.count} rows
              </span>
            )}
            {e.fix_hint && <div className="mt-0.5 italic opacity-80">{e.fix_hint}</div>}
            {e.rows.length > 0 && (
              <div className="mt-0.5 font-mono text-xs opacity-70">
                {e.rows.join(', ')}
                {e.count > e.rows.length && ' …'}
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-red-700 dark:text-red-400">
        Most of these are configuration rather than bad data:{' '}
        {CONFIG_PAGES.map((p, i) => (
          <React.Fragment key={p.to}>
            {i > 0 && ', '}
            <Link to={p.to} className="underline">{p.label}</Link>
          </React.Fragment>
        ))}
        . A mapping whose template — or whose OVERWEIGHT template — has been
        deleted does not count as mapped.
      </p>
    </div>
  );
};

const DaltileOtwUploadPage: React.FC = () => {
  const [ref, setRef] = useState<DaltileOtwReferenceData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<'validate' | 'apply' | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<ValidateResponse | null>(null);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  const [filter, setFilter] = useState<Verdict | 'all'>('all');
  const [drawerPro, setDrawerPro] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // Elapsed seconds while a run is going. A chunk is 50 containers, and against
  // a REMOTE database from a laptop a create costs ~5s (measured: 49ms per
  // statement x ~103 statements), so one chunk can sit silent for four minutes
  // and read as hung. In-region it is a fraction of that. A ticking clock is the
  // cheap honest signal that something is still happening either way.
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (busy !== 'apply') return undefined;
    const started = Date.now();
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [busy]);

  useEffect(() => {
    getReferenceData().then(setRef).catch(() => setRef(null));
  }, []);

  const onPickFile = (f: File | null) => {
    setFile(f);
    setPreview(null);
    setResult(null);
    setFilter('all');
    setErrorMsg(null);
    setProgress(null);
    setElapsed(0);
  };

  const onValidate = async () => {
    if (!file) return;
    setErrorMsg(null);
    setResult(null);
    setBusy('validate');
    try {
      setPreview(await validateFile(file));
    } catch (e) {
      setPreview(null);
      setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
    }
  };

  // Chunked. One request per chunk keeps every request short whatever the
  // latency, and gives the user a progress count instead of a spinner that looks
  // hung on a file of several hundred containers.
  const onApply = async () => {
    if (!file || !preview) return;
    setErrorMsg(null);
    setBusy('apply');

    const toWrite = preview.containers
      .filter((c) => c.verdict === 'create' || c.verdict === 'update')
      .map((c) => c.container);
    const chunks: string[][] = [];
    for (let i = 0; i < toWrite.length; i += CHUNK_SIZE) {
      chunks.push(toWrite.slice(i, i + CHUNK_SIZE));
    }

    setProgress({ done: 0, total: toWrite.length });
    const merged: ApplyResponse = {
      ok: true, filename: file.name, batch_id: '', created: 0, updated: 0,
      skipped: 0, failed: 0, mr_lines: 0, results: [], pronumbers: [], errors: [],
    };
    try {
      for (let i = 0; i < chunks.length; i += 1) {
        const res = await applyFile(file, {
          containers: chunks[i],
          batchId: merged.batch_id || undefined,
          final: i === chunks.length - 1,
          // What the earlier chunks already did, so the final one can record a
          // System Event describing the RUN rather than its last slice.
          soFar: { created: merged.created, updated: merged.updated,
                   mrLines: merged.mr_lines },
        });
        if (!res.ok && res.errors.length) {
          setResult(res);
          setPreview(null);
          return;
        }
        merged.batch_id = merged.batch_id || res.batch_id;
        merged.created += res.created;
        merged.updated += res.updated;
        merged.failed += res.failed;
        merged.mr_lines += res.mr_lines;
        // No client-side filtering: the server processed exactly this chunk, so
        // what it returns IS this chunk's result. Second-guessing it here only
        // drops rows when the two disagree.
        merged.results.push(...res.results);
        merged.pronumbers.push(...res.pronumbers);
        merged.ok = merged.ok && res.ok;
        setProgress({ done: Math.min((i + 1) * CHUNK_SIZE, toWrite.length), total: toWrite.length });
        // Show what has landed so far, so a long run is visibly progressing.
        setResult({ ...merged });
      }
      merged.skipped = preview.containers.length - toWrite.length;
      setResult({ ...merged });
    } catch (e) {
      setErrorMsg(extractDetail(e));
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const willWrite = useMemo(
    () => (preview ? preview.counts.create + preview.counts.update : 0),
    [preview],
  );

  const matching = useMemo(() => {
    if (!preview) return [];
    return filter === 'all'
      ? preview.containers
      : preview.containers.filter((c) => c.verdict === filter);
  }, [preview, filter]);

  // Writers first within the current filter, so the rows that matter are the
  // ones that survive the cap.
  const shown = useMemo(() => {
    if (matching.length <= MAX_ROWS) return matching;
    const writes = matching.filter((c) => c.verdict === 'create' || c.verdict === 'update');
    const rest = matching.filter((c) => c.verdict !== 'create' && c.verdict !== 'update');
    return [...writes, ...rest].slice(0, MAX_ROWS);
  }, [matching]);

  const overweightCount = useMemo(
    () => (preview ? preview.containers.filter((c) => c.overweight).length : 0),
    [preview],
  );

  const openAllPros = (pros: string[]) => {
    if (pros.length) openProSearch(pros);
  };

  return (
    <div className="mx-auto max-w-[1600px] p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-heading">Dal Tile OTW Upload</h1>
        <p className="mt-1 text-sm text-muted">
          Upload the OTW workbook to create new orders and bring existing ones up to
          date. Each container becomes one pro; its rows are per purchase order, so
          weights and pallet counts are added together and each PO becomes a
          master-receiving line.
        </p>
      </header>

      {/* ── file picker ─────────────────────────────────────────────── */}
      <section className="mb-6 rounded-lg border border-rule bg-panel p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-rule px-4 py-2 text-sm font-medium text-body hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowUpTrayIcon className="h-4 w-4" />
            Choose file
          </button>
          <span className="text-sm text-muted">
            {file ? file.name : 'No file chosen'}
          </span>
          <div className="ml-auto flex gap-3">
            <button
              onClick={onValidate}
              disabled={!file || busy !== null}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-50"
            >
              {busy === 'validate' ? 'Checking…' : 'Review'}
            </button>
            <button
              onClick={onApply}
              disabled={!preview?.ok || willWrite === 0 || busy !== null}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {busy === 'apply'
                ? (progress
                    ? `Running… ${progress.done}/${progress.total} · ${formatElapsed(elapsed)}`
                    : 'Running…')
                : `Run import${willWrite ? ` (${willWrite})` : ''}`}
            </button>
          </div>
        </div>
        {ref && (
          <p className="mt-3 text-xs text-muted">
            .xlsx, .xls or .csv. {ref.columns.length} columns in any order, all required
            to be present; {ref.required_columns.length} must carry a value:{' '}
            <span className="font-mono">{ref.required_columns.join(', ')}</span>.{' '}
            <span className="font-mono">{ref.optional_columns.join(', ')}</span> is
            picked up when present. The header may sit below a title block, and every
            worksheet in the workbook is read.
          </p>
        )}
      </section>

      {errorMsg && (
        <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
          {errorMsg}
        </div>
      )}

      {preview && !preview.ok && preview.errors.length > 0 && (
        <div className="mb-6"><ErrorList errors={preview.errors} /></div>
      )}
      {result && !result.ok && result.errors.length > 0 && (
        <div className="mb-6"><ErrorList errors={result.errors} /></div>
      )}

      {/* ── results of a run ────────────────────────────────────────── */}
      {result && result.ok && (
        <section className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/30">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-800 dark:text-green-300">
            <CheckCircleIcon className="h-5 w-5" />
            Imported {result.filename} — {result.created} created, {result.updated} updated,
            {' '}{result.skipped} unchanged
            {result.mr_lines > 0 && `, ${result.mr_lines} MR line${result.mr_lines === 1 ? '' : 's'}`}
            {result.failed > 0 && `, ${result.failed} failed`}.
          </div>
          {result.pronumbers.length > 0 && (
            <button
              onClick={() => openAllPros(result.pronumbers)}
              className="mt-2 inline-flex items-center gap-1 text-sm text-accent-text underline"
            >
              Open all {result.pronumbers.length} in Search Results
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {result.results.filter((r) => r.pronumber && r.ok).map((r) => (
              <button
                key={r.container}
                onClick={() => setDrawerPro(Number(r.pronumber))}
                className="rounded border border-green-300 bg-white px-2 py-1 font-mono text-xs text-accent-text hover:bg-green-100 dark:border-green-700 dark:bg-gray-800"
                title={`${r.container} — ${r.action}${r.mr_lines ? `, ${r.mr_lines} MR` : ''}`}
              >
                {r.pronumber}
              </button>
            ))}
          </div>
          {result.results.some((r) => r.went_overweight) && (
            <div className="mt-3 flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-400">
              <ScaleIcon className="h-4 w-4" />
              {result.results.filter((r) => r.went_overweight).length} pro
              {result.results.filter((r) => r.went_overweight).length === 1 ? '' : 's'} went
              overweight when the new receiving lines were added — the CSR has been
              e-mailed to reprice.
            </div>
          )}
          {result.failed > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-red-800 dark:text-red-300">
              {result.results.filter((r) => !r.ok).map((r) => (
                <li key={r.container}>
                  <span className="font-mono">{r.container}</span>: {r.error}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── the plan ────────────────────────────────────────────────── */}
      {preview?.ok && (
        <section className="rounded-lg border border-rule bg-panel">
          <div className="flex flex-wrap items-center gap-2 border-b border-rule p-4">
            <button
              onClick={() => setFilter('all')}
              className={`rounded-full px-3 py-1 text-xs font-medium ${filter === 'all' ? 'bg-accent text-accent-fg' : 'bg-gray-100 text-body dark:bg-gray-700'}`}
            >
              All {preview.containers.length}
            </button>
            {VERDICT_ORDER.map((v) => {
              const n = preview.counts[v] ?? 0;
              if (!n) return null;
              return (
                <button
                  key={v}
                  onClick={() => setFilter(v)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${filter === v ? 'bg-accent text-accent-fg' : VERDICT_STYLES[v]}`}
                >
                  {VERDICT_LABELS[v]} {n}
                </button>
              );
            })}
            <span className="ml-auto text-xs text-muted">
              {preview.total_rows} row{preview.total_rows === 1 ? '' : 's'}
              {preview.sheets.length > 1 && ` over ${preview.sheets.length} sheets`} →{' '}
              {preview.containers.length} container{preview.containers.length === 1 ? '' : 's'}
              {preview.mr_lines > 0 && `, ${preview.mr_lines} MR line${preview.mr_lines === 1 ? '' : 's'}`}
            </span>
          </div>

          {(preview.eta_warnings > 0 || preview.eta_past_warnings > 0
            || overweightCount > 0 || preview.legacy_divergences > 0) && (
            <div className="space-y-1 border-b border-rule px-4 py-2 text-xs">
              {preview.eta_warnings > 0 && (
                <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {preview.eta_warnings} container{preview.eta_warnings === 1 ? ' has an ETA' : 's have ETAs'}
                  {' '}more than 60 days out. They are still imported.
                </div>
              )}
              {preview.eta_past_warnings > 0 && (
                <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {preview.eta_past_warnings} container
                  {preview.eta_past_warnings === 1 ? ' has an ETA' : 's have ETAs'} more than
                  60 days in the past. They are still imported.
                </div>
              )}
              {overweightCount > 0 && ref && (
                <div className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <ScaleIcon className="h-4 w-4" />
                  {overweightCount} container{overweightCount === 1 ? ' is' : 's are'} over{' '}
                  {ref.overweight_lbs.toLocaleString()} lbs and will use the overweight
                  template.
                </div>
              )}
              {preview.legacy_divergences > 0 && (
                <div className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400">
                  <InformationCircleIcon className="h-4 w-4" />
                  Legacy CMS could have decided {preview.legacy_divergences} container
                  {preview.legacy_divergences === 1 ? '' : 's'} differently — marked below.
                  Useful when comparing a run side by side.
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-rule bg-gray-50 text-xs uppercase text-muted dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left">Container</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">IDC</th>
                  <th className="px-3 py-2 text-left">SCAC</th>
                  <th className="px-3 py-2 text-left">Port</th>
                  <th className="px-3 py-2 text-left">Template</th>
                  <th className="px-3 py-2 text-right">Weight</th>
                  <th className="px-3 py-2 text-left">BOL</th>
                  <th className="px-3 py-2 text-left">PO</th>
                  <th className="px-3 py-2 text-left">ETA</th>
                  <th className="px-3 py-2 text-left">Detail</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <ContainerRow key={c.container} c={c} onOpenPro={setDrawerPro} />
                ))}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-3 py-6 text-center text-muted">
                      No containers with that verdict.
                    </td>
                  </tr>
                )}
                {matching.length > shown.length && (
                  <tr>
                    <td colSpan={11} className="px-3 py-4 text-center text-sm text-muted">
                      Showing {shown.length} of {matching.length} containers —
                      everything that will be created or updated is listed first.
                      Use the buttons above to narrow the list. All
                      {' '}{matching.length} are included in the run.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={() => setDrawerPro(null)}
      />
    </div>
  );
};

const ContainerRow: React.FC<{
  c: ContainerPreview;
  onOpenPro: (pro: number) => void;
}> = ({ c, onOpenPro }) => (
  <tr className="border-b border-rule last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
    <td className="px-3 py-2 font-mono">
      {c.container}
      <div className="text-[0.65rem] text-muted">{c.rows.join(', ')}</div>
    </td>
    <td className="px-3 py-2"><VerdictPill v={c.verdict} /></td>
    <td className="px-3 py-2">{c.rdc}</td>
    <td className="px-3 py-2">{c.scac}</td>
    <td className="px-3 py-2 text-xs">{c.port_of_discharge}</td>
    <td className="px-3 py-2 font-mono text-xs">
      {c.template}
      {c.overweight && (
        <span className="ml-1 rounded bg-amber-100 px-1 text-[0.6rem] font-sans font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          OW
        </span>
      )}
    </td>
    <td className="whitespace-nowrap px-3 py-2 text-right text-xs">
      {Number(c.lbs || 0).toLocaleString()}
      <div className="text-[0.65rem] text-muted">{c.piececount} plt</div>
    </td>
    <td className="px-3 py-2 text-xs">{c.bol}</td>
    <td className="px-3 py-2 text-xs">{c.po}</td>
    <td className="px-3 py-2 text-xs">
      {c.etadate}
      {(c.eta_warning || c.eta_past_warning) && (
        <span
          className="ml-1 text-amber-600 dark:text-amber-400"
          title={c.eta_warning ? 'More than 60 days out' : 'More than 60 days in the past'}
        >
          !
        </span>
      )}
    </td>
    <td className="px-3 py-2 text-xs">
      <div className="text-muted">{c.reason}</div>
      {c.pronumbers.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {c.pronumbers.map((p) => (
            <button
              key={p}
              onClick={() => onOpenPro(Number(p))}
              className="font-mono text-accent-text underline"
            >
              {p}
            </button>
          ))}
        </div>
      )}
      {c.changes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {c.changes.map((ch) => (
            <li key={ch.column} className="font-mono text-[0.65rem]">
              <span className="text-muted">{ch.column}:</span>{' '}
              <span className="line-through opacity-60">{ch.old || '—'}</span>{' '}
              → <span className="text-body">{ch.new}</span>
            </li>
          ))}
        </ul>
      )}
      {c.mr_lines > 0 && (
        <div className="mt-0.5 flex items-center gap-1 text-[0.65rem] text-blue-700 dark:text-blue-400">
          <ArchiveBoxIcon className="h-3 w-3" />
          {c.mr_lines} receiving line{c.mr_lines === 1 ? '' : 's'}
          {c.warehouse && ` into ${c.warehouse}`}
        </div>
      )}
      {c.discrepancies.length > 0 && (
        <div className="mt-0.5 text-[0.65rem] text-amber-700 dark:text-amber-400">
          {/* Reported to the CSR by e-mail; the run does not change these. */}
          Differs from the pro:{' '}
          {c.discrepancies.map((d) => `${d.label} ${d.in_system || '—'} → ${d.on_upload || '—'}`).join('; ')}
        </div>
      )}
      {c.conflicts.length > 0 && (
        <div className="mt-0.5 text-[0.65rem] text-amber-700 dark:text-amber-400">
          Rows disagree on {c.conflicts.join(', ')} — the last one wins
        </div>
      )}
      {c.legacy_divergence && (
        <div className="mt-0.5 text-[0.65rem] text-blue-700 dark:text-blue-400">
          {c.legacy_divergence}
        </div>
      )}
    </td>
  </tr>
);

export default DaltileOtwUploadPage;
